import useConfig from 'hooks/useConfig';
import React, {
  useEffect, useState, useMemo, useCallback, useContext,
} from 'react';
import { AddressContext, AuthContext } from 'constants/contexts';
import { captureException } from 'utils/error';
import usePersistedState from 'hooks/usePersistedState';
import useEventEffect from 'hooks/useEvent';
import { EVENTS } from 'constants/events';
import { truncate } from 'lodash-es';
import useParents from './useParents';
import { addAddress, deleteAddress, getUserAddresses } from './requests';

import AddressOverlay from './AddressOverlay';

function AddressProvider({ children }) {
  const { isLoggedIn } = useContext(AuthContext);
  const [version, setVersion] = useState(0);
  const [deletedIds, setDeletedIds] = useState([]);
  const [unfilteredAddresses, setAddresses] = usePersistedState(
    [],
    'address-provider/addresses',
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [currentAddress, setCurrentAddress] = usePersistedState(
    null,
    'address-provider/current-addresses',
  );
  const refetch = useCallback(() => setVersion((v) => v + 1), []);
  const addresses = useMemo(() => unfilteredAddresses.filter((adr) => !deletedIds?.includes(adr.id)), [deletedIds, unfilteredAddresses]);
  const parentIds = useParents(currentAddress);

  useEffect(() => {
    if (!currentAddress && addresses.length) {
      setCurrentAddress(addresses[0]);
    }
  }, [addresses, currentAddress]);

  const handleDelete = useCallback(({ id }) => {
    setDeletedIds((current) => [...current, id]);
    deleteAddress(id)
      .then((res) => {
        refetch();
      })
      .catch((err) => {
        captureException(err);
        setDeletedIds((current) => [...current.filter((i) => i !== id)]);
      });
  }, [currentAddress, setCurrentAddress, refetch]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    getUserAddresses()
      .then((res) => {
        setAddresses(res.addresses);
        if (res.lastAddress && !currentAddress) {
          setCurrentAddress(res.lastAddress);
        } else if (
          !res.addresses.some((adr) => adr.id === currentAddress?.id)
        ) {
          setCurrentAddress(res.lastAddress || res.addresses[0]);
        }
      })
      .catch((err) => {
        captureException(err);
        console.error(err);
      });
  }, [isLoggedIn, modalOpen, version]);

  useEffect(() => {
    if (!isLoggedIn || !currentAddress || currentAddress.id) {
      return;
    }
    addAddress(currentAddress).then((adr) => {
      setCurrentAddress(adr);
      setAddresses((current) => ([adr, ...current]));
    });
  }, [isLoggedIn, currentAddress, setCurrentAddress, setAddresses]);

  useEventEffect(() => {
    setCurrentAddress(null);
    setAddresses([]);
  }, EVENTS.SIGN_OUT);

  const openModal = useCallback(() => setModalOpen(true), []);

  const contextValue = useMemo(
    () => ({
      editingAddress,
      setEditingAddress,
      openModal,
      modalOpen,
      deleteAddress: handleDelete,
      currentAddress,
      setAddress(adr) {
        setCurrentAddress({
          ...adr,
          label: truncate(adr.label || adr.text, { length: 30 }),
        });
      },
      closeModal() {
        setModalOpen(false);
        setTimeout(() => {
          setEditingAddress(null);
        }, 500);
      },
      addresses,
      parentIds,
      refetch,
    }),
    [
      modalOpen,
      refetch,
      editingAddress,
      parentIds,
      openModal,
      addresses,
      handleDelete,
      currentAddress,
      setCurrentAddress,
    ],
  );

  return (
    <AddressContext.Provider value={contextValue} displayName="AddressProvider">
      <AddressOverlay />
      {children}
    </AddressContext.Provider>
  );
}

export default AddressProvider;
